import styled from "@emotion/styled";
import { forwardRef } from "react";
import { Form, OverlayTrigger, Tooltip } from "react-bootstrap";
import { FloatingLabel } from "./FloatingLabel";

// define the two size variants
const sizeStyles = {
  sm: {
    fontSize: "0.875rem",
    padding: "0.25rem 0.5rem",
    height: "38px",
  },
  lg: {
    fontSize: "1rem",
    padding: "0.5rem 1rem",
    height: "48px",
  },
};

// styled version of Form.Control that applies sizeStyles based on our `inputSize` prop
const StyledControl = styled(Form.Control, {
  // prevent our custom prop from leaking to the DOM
  shouldForwardProp: (prop) => prop !== "inputSize",
})`
  height: ${({ inputSize }) => sizeStyles[inputSize].height} !important;
  min-height: ${({ inputSize }) => sizeStyles[inputSize].height};
  font-size: ${({ inputSize }) => sizeStyles[inputSize].fontSize};
  padding: ${({ inputSize }) => sizeStyles[inputSize].padding};
  border-radius: 0.25rem;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus {
    border-color: #80bdff;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
  }

  &:disabled,
  &[readonly] {
    background-color: #e9ecef;
    opacity: 1;
  }
`;

export const TextField = forwardRef(
  (
    {
      readOnly,
      name,
      label,
      size = "lg",
      disabled = false,
      error,
      helperText,
      ...props
    },
    ref
  ) => {
    const control = (
      <StyledControl
        readOnly={readOnly}
        isInvalid={!!error}
        name={name}
        inputSize={size}
        placeholder={label || props.placeholder || ""}
        ref={ref}
        disabled={disabled}
        {...props}
      />
    );

    const renderTooltip = (ttProps) => (
      <Tooltip id="error-tooltip" {...ttProps}>
        {helperText}
      </Tooltip>
    );

    const wrappedControl = helperText ? (
      <OverlayTrigger placement="top" overlay={renderTooltip}>
        {control}
      </OverlayTrigger>
    ) : (
      control
    );

    return (
      <Form.Group>
        {label ? (
          <>
            <FloatingLabel controlId={name} label={label} size={size}>
              {wrappedControl}
            </FloatingLabel>
            {error && (
              <Form.Control.Feedback type="invalid" className="d-block">
                {error}
              </Form.Control.Feedback>
            )}
          </>
        ) : (
          <>
            {wrappedControl}
            {error && (
              <Form.Control.Feedback type="invalid" className="d-block">
                {error}
              </Form.Control.Feedback>
            )}
          </>
        )}
      </Form.Group>
    );
  }
);

export default TextField;
